import { useState } from 'react';
import { Landmark, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SUBSCRIPTION_TIER_LABELS, hasEnterpriseIdentityTier } from '@reflex/shared';
import { getGetCompanyQueryKey, useGetCompany } from '@reflex/ui/client/generated/company';
import { COMPANY_FALLBACK_TITLE, COMPANY_SCOPE_DESCRIPTION } from '@reflex/ui/client/company';
import { companyRefusalMessage } from '@reflex/ui/client/entitlements';
import { CanGuard } from '@reflex/ui/client/permissions';
import { CompanyDomainClaims } from '@/components/billing-entities/domain-claims/CompanyDomainClaims';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { Banner } from '@reflex/ui/components/shared/Banner';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { HubReadGuard } from '@reflex/ui/components/layout/NoReadAccessState';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { RequireActiveOrganization } from '@/components/shared/RequireActiveOrganization';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { AddCompanyOrganizationDialog } from './AddCompanyOrganizationDialog';

/**
 * Configure → Company → Overview: what the company owning this organization
 * carries, and which organizations that covers.
 *
 * A company is above the organization. It holds the commercial relationship
 * and the shared identity trust, while membership, roles, agents, and secrets
 * stay inside each organization. Until now that level had no page of its own —
 * its settings were reachable only as cards on organization and profile pages,
 * which made company-wide changes look organization-wide. This page and its
 * sibling row, Security, are the company's own surface.
 *
 * The active organization is the door: the read is authorized against this
 * organization's `company:read` and the server takes the company from the
 * caller's membership, so there is no company id in the URL or on the wire.
 * Every organization under the company is an equally valid door.
 */
export function CompanyPage() {
  // Organization guard outermost: permissions are org-scoped, so with no
  // active organization there is no permission set to check and `HubReadGuard`
  // would report a missing `company:read` when the real answer is "pick an
  // organization first".
  return (
    <RequireActiveOrganization title={COMPANY_FALLBACK_TITLE}>
      {(org) => (
        <HubReadGuard permission="company:read">
          <CompanyBody key={org.id} organizationId={org.id} />
        </HubReadGuard>
      )}
    </RequireActiveOrganization>
  );
}

function CompanyBody({ organizationId }: { organizationId: string }) {
  const routes = useOrgRoutes();
  const [isAddOrganizationOpen, setIsAddOrganizationOpen] = useState(false);
  // The generated key is static, but the response is filtered by the active
  // organization the client sends: without the org in the key, switching to an
  // organization in another company renders the previous company's name, plan,
  // and sibling list from cache until the refetch lands.
  const query = useGetCompany({
    query: { queryKey: [...getGetCompanyQueryKey(), organizationId], retry: false },
  });

  // An organization with no company is refused with 409
  // `billing_entity_required`, which is an answer and not an outage: no retry
  // attaches a company. It is reachable in production — migration `0109`
  // leaves `organizations.billing_entity_id` nullable for its bake period, so
  // an org created by an old pod mid-rollout lands here — so say what is
  // wrong instead of offering a Retry that re-issues the same refusal.
  const refusal = companyRefusalMessage(query.error);

  return (
    <PageLayout maxWidth="full">
      <div className="mx-auto flex w-full max-w-4xl">
        <div className="min-w-0 flex-1">
          <PageHeader
            iconVariant="box"
            icon={<Landmark className="h-5 w-5 text-muted-foreground" />}
            // The company's own name once it is known: the nav group already
            // says "Company", so the header can say which one. Falls back
            // while loading, and for a caller whose organization has none.
            title={query.data?.name ?? COMPANY_FALLBACK_TITLE}
            description={COMPANY_SCOPE_DESCRIPTION}
          />
          <div className="mt-6 space-y-8">
            {refusal ? (
              <Banner intent="info" data-testid="company-refusal">
                {refusal}
              </Banner>
            ) : (
              <AsyncContent
                isLoading={query.isLoading}
                isError={query.isError}
                loadingMessage="Loading company…"
                errorMessage="Could not load the company."
              >
                {query.data && (
                  <>
                    <SectionCard
                      title="Details"
                      description="One contract, one plan, and one shared identity provider, however many organizations you run."
                    >
                      <div className="flex flex-col gap-4" data-testid="company-summary">
                        <Field label="Name">
                          <span className="text-sm" data-testid="company-name">
                            {query.data.name}
                          </span>
                        </Field>
                        <Field label="Plan">
                          <Badge variant="secondary" data-testid="company-tier">
                            {SUBSCRIPTION_TIER_LABELS[query.data.subscriptionTier]}
                          </Badge>
                        </Field>
                        {/* What the plan means for the sibling row, derived
                            from the tier this page already has rather than a
                            second read. Company → Security states the same
                            fact when a save is refused; stating it here first
                            is what makes that notice expected instead of a
                            surprise at the end of filling in a form. */}
                        <p className="text-xs text-muted-foreground" data-testid="company-features">
                          {hasEnterpriseIdentityTier(query.data.subscriptionTier)
                            ? 'Includes enterprise identity: connect your identity provider under '
                            : 'Enterprise identity is not included on this plan. Talk to your Reflex account team to turn it on for your company, then connect a provider under '}
                          <Link className="underline" to={routes.companySecurity}>
                            Security
                          </Link>
                          .
                        </p>
                      </div>
                    </SectionCard>

                    <SectionCard
                      title="Organizations"
                      description="A company-level change reaches every organization listed here. Each one keeps its own members, roles, and data."
                      // Growing the company belongs on the card that shows
                      // what growing it costs: the list below is the blast
                      // radius every company-level setting already covers.
                      action={
                        <CanGuard permission="company:manage">
                          <Button
                            size="sm"
                            onClick={() => setIsAddOrganizationOpen(true)}
                            data-testid="add-company-organization"
                          >
                            <Plus className="icon-sm" />
                            Add organization
                          </Button>
                        </CanGuard>
                      }
                    >
                      <ul className="flex flex-col gap-3" data-testid="company-organizations">
                        {query.data.organizations.map((organization) => (
                          <li
                            key={organization.id}
                            className="flex items-baseline justify-between gap-4"
                          >
                            <span className="text-sm">{organization.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {organization.slug}
                            </span>
                          </li>
                        ))}
                        {/* The active organization is always in this list, so
                            an empty one means the roster and the membership
                            that authorized the read disagree — say so rather
                            than rendering a blank card. */}
                        {query.data.organizations.length === 0 && (
                          <li className="text-sm text-muted-foreground">
                            No organizations to show.
                          </li>
                        )}
                      </ul>
                    </SectionCard>

                    {/* Mounted with the loaded company so the dialog can name
                        it, and so a caller looking at a refusal or a failed
                        read is never offered a write that would fail too. */}
                    <AddCompanyOrganizationDialog
                      open={isAddOrganizationOpen}
                      onOpenChange={setIsAddOrganizationOpen}
                      companyName={query.data.name}
                      organizationId={organizationId}
                    />

                    {/* Domain ownership and the routing it unlocks: one
                        surface, one table, DNS proof included. */}
                    <CompanyDomainClaims organizationId={organizationId} />
                  </>
                )}
              </AsyncContent>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 max-w-md">
      <SectionLabel as="h3" tone="strong">
        {label}
      </SectionLabel>
      {children}
    </div>
  );
}
